import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SummariesToolbar } from '../components/summaries/SummariesToolbar';
import { SummaryCard, CoverSkeleton, EmptyShelf } from '../components/summaries/SummaryCard';
import { SummaryDrawer } from '../components/summaries/SummaryDrawer';
import { OliveBranch } from '../components/summaries/BookCover';
import { FILTER_KEYS } from '../lib/summaries';

const PAGE = 24;

const useColumns = () => {
  const calc = () => (window.matchMedia('(min-width: 1024px)').matches ? 4 : window.matchMedia('(min-width: 768px)').matches ? 3 : 2);
  const [cols, setCols] = useState(calc);
  useEffect(() => {
    const fn = () => setCols(calc());
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return cols;
};

const Stat = ({ value, label, testId }) => (
  <div className="text-center" data-testid={testId}>
    <div className="text-xl md:text-2xl font-bold text-[#3A5F0B] tabular-nums">{(value ?? 0).toLocaleString('ar-OM')}</div>
    <div className="text-[11px] text-muted-foreground">{label}</div>
  </div>
);

export default function SummariesPage() {
  const [params, setParams] = useSearchParams();
  const { summaryId } = useParams();
  const navigate = useNavigate();
  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ''])), [params]);
  const [meta, setMeta] = useState({ stats: {}, categories: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);
  const [likers, setLikers] = useState(null);
  const [compact, setCompact] = useState(false);
  const cols = useColumns();
  const sentinel = useRef(null);
  const loadingMore = useRef(false);

  const setFilters = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  }, [params, setParams]);

  const query = useCallback((skip) => {
    const p = { skip, limit: PAGE };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = ['mine', 'liked', 'with_image'].includes(k) ? true : v; });
    return api.get('/summaries', { params: p });
  }, [filters]);

  useEffect(() => { api.get('/summaries/meta').then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    query(0).then((r) => {
      if (cancelled) return;
      setItems(r.data.items); setTotal(r.data.total); setHasMore(r.data.has_more);
    }).catch(() => toast.error('تعذّر تحميل الملخصات')).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [query]);

  const loadMore = useCallback(async () => {
    if (loadingMore.current || !hasMore) return;
    loadingMore.current = true;
    try {
      const r = await query(items.length);
      setItems((prev) => [...prev, ...r.data.items.filter((i) => !prev.some((p) => p.id === i.id))]);
      setHasMore(r.data.has_more);
    } finally { loadingMore.current = false; }
  }, [query, items.length, hasMore]);

  useEffect(() => {
    if (!sentinel.current) return undefined;
    const io = new IntersectionObserver((e) => e[0].isIntersecting && loadMore(), { rootMargin: '600px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [loadMore]);

  useEffect(() => {
    const onScroll = (e) => {
      const el = e.target === document ? document.documentElement : e.target;
      if (el && typeof el.scrollTop === 'number') setCompact(el.scrollTop > 120);
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  useEffect(() => {
    if (!summaryId) { setOpenItem(null); return; }
    const found = items.find((i) => i.id === summaryId);
    if (found) { setOpenItem(found); return; }
    api.get(`/summaries/${summaryId}`).then((r) => setOpenItem(r.data)).catch(() => toast.error('الملخص غير موجود'));
  }, [summaryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const qs = params.toString() ? `?${params.toString()}` : '';
  const open = (item) => navigate(`/summaries/${item.id}${qs}`);
  const close = () => navigate(`/summaries${qs}`);

  const patchItem = (id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    setOpenItem((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  };

  const like = async (item) => {
    const want = !item.liked_by_me;
    patchItem(item.id, { liked_by_me: want, likes_count: item.likes_count + (want ? 1 : -1) });
    try {
      const r = await api.post(`/summaries/${item.id}/like`, { like: want });
      patchItem(item.id, { liked_by_me: r.data.liked, likes_count: r.data.likes_count });
    } catch {
      patchItem(item.id, { liked_by_me: item.liked_by_me, likes_count: item.likes_count });
      toast.error('تعذّر حفظ الإعجاب');
    }
  };

  const showLikers = async (item) => {
    if (!item.likes_count) return;
    try { const r = await api.get(`/summaries/${item.id}/likers`); setLikers({ title: item.title, names: r.data.likers }); } catch { /* ignore */ }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pb-16" dir="rtl" data-testid="summaries-page">
      <header className="relative overflow-hidden flex flex-col sm:flex-row sm:items-end justify-between gap-5 pt-6 pb-6">
        <OliveBranch className="absolute -left-4 -top-6 h-40 text-[#3A5F0B]/10 -rotate-12 pointer-events-none" />
        <div>
          <p className="ds-kicker">مكتبة المبادرة</p>
          <h1 className="text-3xl md:text-4xl font-bold text-accent">الملخصات والاقتباسات</h1>
          <p className="text-sm text-muted-foreground mt-2">ما استفاده القرّاء والقارئات من قراءاتهم، في مكان واحد.</p>
        </div>
        <div className="flex items-center gap-6 md:gap-8 bg-white/70 rounded-2xl border border-[#C19A6B]/15 px-5 py-3 shadow-[0_2px_10px_-6px_rgba(93,64,55,0.25)]">
          <Stat value={meta.stats.summaries} label="ملخص" testId="stat-summaries" />
          <Stat value={meta.stats.pages} label="صفحة مقروءة" testId="stat-pages" />
          <Stat value={meta.stats.readers} label="قارئ وقارئة" testId="stat-readers" />
        </div>
      </header>

      <SummariesToolbar filters={filters} setFilters={setFilters} categories={meta.categories} total={meta.stats.summaries ?? 0} compact={compact} />

      <p className="text-xs text-muted-foreground mt-4 mb-3" data-testid="summaries-total">{loading ? '…' : `${total} ملخص`}</p>
      {loading ? (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 md:gap-6">{Array.from({ length: 8 }).map((_, i) => <CoverSkeleton key={i} />)}</div>
      ) : items.length === 0 ? <EmptyShelf /> : (
        <div className="flex gap-4 md:gap-6 items-start" data-testid="summaries-grid">
          {Array.from({ length: cols }, (_, c) => (
            <div key={c} className="flex-1 min-w-0">
              {items.filter((_, idx) => idx % cols === c).map((i) => <SummaryCard key={i.id} item={i} onOpen={open} onLike={like} onShowLikers={showLikers} />)}
            </div>
          ))}
        </div>
      )}
      <div ref={sentinel} className="h-10" />
      {hasMore && !loading && <div className="columns-2 md:columns-3 lg:columns-4 gap-4 md:gap-6">{Array.from({ length: 4 }).map((_, i) => <CoverSkeleton key={i} />)}</div>}

      <SummaryDrawer item={openItem} onClose={close} onLike={like} onShowLikers={showLikers} />

      <Dialog open={!!likers} onOpenChange={(o) => !o && setLikers(null)}>
        <DialogContent className="max-w-sm" dir="rtl" data-testid="likers-dialog">
          <DialogHeader><DialogTitle className="text-right">أعجب بـ «{likers?.title}»</DialogTitle></DialogHeader>
          <ul className="max-h-72 overflow-y-auto space-y-2 text-sm">
            {likers?.names.map((n, i) => <li key={i} className="py-1.5 border-b border-[#C19A6B]/10">{n}</li>)}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
